import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import flatten from 'lodash/flatten';
import { OperationAnchor } from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import type { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import type { SnapLineContext } from '@src/slide/application/service/snap-line-service/context';
import type {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import type { IRefElement } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import type { ISnapGridLine } from '@src/slide/application/service/snap-line-service/interface/line.interface';
import type { OperationType } from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';

export abstract class ElementRefer {
    public refs: IRefElement[];
    public refGrids: ISnapGridLine[];
    // 作为一批 ref 的标识，可以基于 ref 不变做一些缓存策略
    public referId: string;
    public referChanged = true;
    public constructor(protected context: SnapLineContext) {}

    public gridifyElements(elements: IRefElement[]): ISnapGridLine[] {
        return flatten(elements.map(ref => this.gridify(
            ref.outerFrameOnSlide,
            ref,
            OperationAnchor.ALL,
        )));
    }

    public reset() {
        this.refs = [];
        this.refGrids = [];
        this.referId = '';
        this.referChanged = true;
    }

    protected isGroup(ele: IElementBaseModel): ele is IGroupModel {
        return ele.type === UIModelType.GROUP;
    }

    protected getSelectElement(): IElementBaseModel {
        return this.context.selectionService.getSelectedModel() as IElementBaseModel;
    }

    protected isElementSelected(ele: IElementBaseModel) {
        return !!this.context.selectionService.selectedElements
            .selectedArray?.some(e =>  e.key === ele.key);
    }

    protected isChildrenSelected(ele: IGroupModel) {
        return this.context.selectionService.selectedElements
            .selectedArrayForType(SelectedEleMgrType.GROUP)?.[0]?.parent?.key === ele.key;
    }

    protected setRefs(refElements: IRefElement[]) {
        const refers = this.simplifyRefs(refElements);
        this.refs = refers;
        this.refGrids = this.gridifyElements(refers);

        this.setReferId();
    }

    private setReferId() {
        const referId = this.refs.map(ref => (ref.ele as IElementBaseModel).key + ref.source).join('-');
        this.referChanged = this.referId !== referId;
        this.referId = referId;
    }

    /**
     * 简化参照元素，减少参照元素个数来降低吸附线复杂度
     */
    private simplifyRefs(refElements: IRefElement[]) {
        if (this.isNumerousElements(refElements)) {
            return refElements.filter(ref => !this.isTinyElement(ref.ele as IElementBaseModel));
        }

        return refElements;
    }

    /**
     * 是否为大量元素的场景
     */
    private isNumerousElements(elements: IRefElement[]) {
        return elements.length >= 40;
    }

    private isTinyElement(element: IElementBaseModel) {
        const { height, width } = element.props.style;
        const { slideWidth, slideHeight } = this.context.uiDataService.presentation.props;
        const limitHeight = slideHeight / 20;
        const limitWidth = slideWidth / 20;

        return height < limitHeight && width < limitWidth;
    }

    /**
     * 参照物网格化逻辑
     */
    public abstract gridify(
        frame: IFrame,
        ref: IRefElement,
        operationAnchorOnSlide: OperationAnchor,
        operationType?: OperationType,
    ): ISnapGridLine[];
}
